import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { getDefaultFinancialsDateRange } from '../../utils/financialsDateRange';
import { exportFinancialsListPdf } from '../../utils/financialsListPdf';
import FinancialsDateRangeFilter from '../../components/FinancialsDateRangeFilter/FinancialsDateRangeFilter';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function formatMoney(value) {
    const num = parseFloat(value);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function IncomeList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const defaultRange = getDefaultFinancialsDateRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [incomeEntries, setIncomeEntries] = useState([]);
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredEntries, setFilteredEntries] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/income', {
                params: { fromDate, toDate }
            });
            setIncomeEntries(res.data);
            setFilteredEntries(res.data);
            setSelectedEntries([]);
        }).catch((err) => {
            console.error('Error fetching income:', err);
            const message = err.response?.data?.Error || err.message || 'Error fetching income';
            alert(message);
        });
    }, [fromDate, toDate, withLoading]);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const results = incomeEntries.filter((entry) =>
            (entry.description || '').toLowerCase().includes(term) ||
            (entry.type || '').toLowerCase().includes(term) ||
            (entry.clientName || '').toLowerCase().includes(term) ||
            (entry.projectName || '').toLowerCase().includes(term)
        );
        setFilteredEntries(results);
    }, [searchTerm, incomeEntries]);

    const handleCheckboxChange = (entryId) => {
        setSelectedEntries((prev) =>
            prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedEntries(e.target.checked ? filteredEntries.map((entry) => entry.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected income entries?')) return;
        Promise.all(selectedEntries.map((entryId) => api.delete(`/income/${entryId}`)))
            .then(() => {
                setIncomeEntries((prev) => prev.filter((entry) => !selectedEntries.includes(entry.id)));
                setSelectedEntries([]);
            })
            .catch(() => {
                alert('Error deleting income entries');
            });
    };

    const allSelected = filteredEntries.length > 0 && selectedEntries.length === filteredEntries.length;

    const handleRangeChange = (nextFromDate, nextToDate) => {
        setFromDate(nextFromDate);
        setToDate(nextToDate);
    };

    const exportIncomePdf = async () => {
        if (filteredEntries.length === 0) {
            alert('No income entries to export');
            return;
        }

        const totalAmount = filteredEntries.reduce(
            (sum, entry) => sum + parseFloat(entry.amount || 0),
            0
        );

        try {
            await exportFinancialsListPdf({
                pageTitle: 'Income',
                fileName: `income_${fromDate}_to_${toDate}.pdf`,
                fromDate,
                toDate,
                searchTerm,
                head: [['Date', 'Amount', 'Type', 'Description', 'Client', 'Project']],
                body: filteredEntries.map((entry) => [
                    formatDateDDMMYYYY(entry.date),
                    formatMoney(entry.amount),
                    entry.type || '',
                    entry.description || '',
                    entry.clientName || '—',
                    entry.projectName || '—'
                ]),
                totalAmount: formatMoney(totalAmount),
                columnStyles: {
                    0: { cellWidth: 22 },
                    1: { halign: 'right' },
                    2: { cellWidth: 18 },
                    5: { cellWidth: 28 }
                }
            });
        } catch (error) {
            console.error('Error exporting income PDF:', error);
            alert('Error exporting income PDF');
        }
    };

    return (
        <PageLoadingArea isLoading={isLoading}>
            <div className="list-page">
                <div className="list-page-header">
                    <div className="list-page-title">
                        <h1>Income</h1>
                        <p className="list-page-subtitle">
                            {filteredEntries.length} entr{filteredEntries.length === 1 ? 'y' : 'ies'} · {formatDateDDMMYYYY(fromDate)} to {formatDateDDMMYYYY(toDate)}
                        </p>
                    </div>
                    <div className="list-header-actions">
                        <div className="list-search-wrap">
                            <input
                                type="text"
                                placeholder="Search description, type, client, or project"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="list-search-input"
                            />
                        </div>
                        <div className="list-button-group">
                            <button
                                type="button"
                                onClick={() => navigate('/financials')}
                                className="list-secondary-button"
                            >
                                Back to financials
                            </button>
                            <button
                                type="button"
                                onClick={exportIncomePdf}
                                className="list-secondary-button"
                                disabled={filteredEntries.length === 0}
                            >
                                Export PDF
                            </button>
                            <button type="button" onClick={() => navigate('/createIncome')} className="list-add-button">
                                Add income
                            </button>
                            {selectedEntries.length > 0 && (
                                <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                    Delete ({selectedEntries.length})
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <FinancialsDateRangeFilter
                    fromDate={fromDate}
                    toDate={toDate}
                    onRangeChange={handleRangeChange}
                />

                <div className="list-table-card">
                    <div className="list-table-scroll">
                        <table className="list-table">
                            <thead>
                                <tr>
                                    <th className="col-check">
                                        <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                    </th>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                    <th>Client</th>
                                    <th>Project</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEntries.length === 0 ? (
                                    <tr className="list-empty-row">
                                        <td colSpan="7">
                                            <div className="list-empty-state">
                                                <h3>No income entries found</h3>
                                                <p>{searchTerm ? 'Try a different search term.' : 'Add your first income entry to get started.'}</p>
                                                {!searchTerm && (
                                                    <button
                                                        type="button"
                                                        className="list-add-button"
                                                        onClick={() => navigate('/createIncome')}
                                                    >
                                                        Add income
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredEntries.map((entry) => (
                                    <tr
                                        key={entry.id}
                                        className={`list-row${selectedEntries.includes(entry.id) ? ' is-selected' : ''}`}
                                        onClick={(e) => {
                                            if (e.target.type !== 'checkbox') navigate(`/createIncome/${entry.id}`);
                                        }}
                                    >
                                        <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedEntries.includes(entry.id)}
                                                onChange={() => handleCheckboxChange(entry.id)}
                                            />
                                        </td>
                                        <td>{formatDateDDMMYYYY(entry.date)}</td>
                                        <td className="col-num">{formatMoney(entry.amount)}</td>
                                        <td>{entry.type}</td>
                                        <td><span className="list-primary-text">{entry.description}</span></td>
                                        <td>{entry.clientName || '—'}</td>
                                        <td>{entry.projectName || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLoadingArea>
    );
}

export default IncomeList;
